/**
 * Prüfprotokoll — Ihre Prüfnotizen und der Abschluss.
 *
 * Der Screen, der aus der Prüfung ein Dokument macht: die fünf Schritte
 * mit Stand, die Zahlen, das Protokoll als PDF — und der Abschluss, der
 * den Bericht des Beirats erzeugt. Wichtig bleibt die Möglichkeit, „mit
 * Vorbehalt" abzuschließen: Ohne sie blockiert eine einzige unbeantwortete
 * Frage die ganze Eigentümerversammlung.
 */

import { useMemo, useState } from 'react';

import { api, datum } from '../lib/api';
import { DEMO } from '../demo/modus';
import { Fortschritt } from '../komponenten/Fortschritt';
import type { Kennzahlen, Kommentar, Pruefung, Rolle, Schritt } from '../lib/typen';

export interface ProtokollProps {
  pruefungId: string;
  pruefung: Pruefung | null;
  kommentare: Kommentar[];
  kennzahlen: Kennzahlen | null;
  schritte: Schritt[];
  rolle: Rolle;
  /** Name des Angemeldeten — für die Vorbelegung des Prüfers im Bericht. */
  name: string;
  onSchritt: (nummer: number) => void;
  onNeuLaden: () => void;
  onMeldung: (text: string) => void;
}

export function Protokoll({ pruefungId, pruefung, kommentare, kennzahlen, schritte, rolle, name, onSchritt, onNeuLaden, onMeldung }: ProtokollProps) {
  const [abschluss, setAbschluss] = useState(false);
  const [anmerkung, setAnmerkung] = useState('');
  const [ort, setOrt] = useState('');
  const [entlastung, setEntlastung] = useState(true);
  const [laeuft, setLaeuft] = useState(false);

  const offene = useMemo(() => kommentare.filter((k) => !k.eltern_id && k.status === 'offen').length, [kommentare]);
  const erledigt = schritte.filter((s) => s.status === 'erledigt').length;
  const alleSchritte = schritte.length > 0 && erledigt >= schritte.length - 1; // Schritt 5 wird mit dem Abschluss erledigt
  const abgeschlossen = pruefung?.status === 'abgeschlossen';
  const letzteAenderung = useMemo(() => {
    const zeiten = [
      ...schritte.map((s) => s.erledigtAm ?? s.begonnenAm),
      ...kommentare.map((k) => k.erstellt_am),
    ].filter((z): z is string => Boolean(z));
    return zeiten.sort().at(-1) ?? null;
  }, [schritte, kommentare]);

  async function abschliessen(urteil: 'ohne_einwand' | 'mit_vorbehalt') {
    setLaeuft(true);
    try {
      await api.abschliessen(pruefungId, urteil, anmerkung, { ort, entlastungEmpfohlen: entlastung });
      setAbschluss(false);
      onNeuLaden();
      onMeldung(
        urteil === 'ohne_einwand'
          ? 'Prüfung ohne Einwand abgeschlossen — der Bericht des Beirats steht im Protokoll.'
          : 'Prüfung mit Vorbehalt abgeschlossen — die offenen Punkte stehen im Protokoll.',
      );
    } catch (g) {
      onMeldung((g as Error).message);
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="wrap" style={{ maxWidth: 920 }}>
      <div className="seitenkopf">
        <div>
          <h2 style={{ margin: 0 }}>Prüfprotokoll</h2>
          <p className="s" style={{ margin: '4px 0 0' }}>Ihre Prüfnotizen — und am Ende der Bericht des Beirats.</p>
        </div>
        <div className="spacer" />
        {!DEMO && (
          <a className="btn" href={api.protokollUrl(pruefungId)} target="_blank" rel="noreferrer">
            ↓ Protokoll drucken
          </a>
        )}
      </div>

      <div className="card" style={{ padding: 18 }}>
        <Fortschritt anteil={kennzahlen?.fortschritt ?? erledigt / Math.max(1, schritte.length)} text={`${erledigt} von ${schritte.length} Prüfbereichen abgeschlossen`} gross />
        <div className="statusband" style={{ marginTop: 14, padding: 0, border: 0, background: 'none' }}>
          <Zahl wert={`${erledigt} / ${schritte.length}`} label="Bereiche abgeschlossen" />
          <Zahl wert={String(kennzahlen?.dokumenteGeprueft ?? 0)} label="Unterlagen geprüft" />
          <Zahl wert={`${kennzahlen?.belegeGeprueft ?? 0} / ${kennzahlen?.belegeGesamt ?? 0}`} label="Belege geprüft" />
          <Zahl wert={String(offene)} label="Rückfragen offen" ton={offene > 0 ? 'w' : undefined} />
          <Zahl wert={letzteAenderung ? datum(letzteAenderung) : '—'} label="Letzte Änderung" />
        </div>
      </div>

      <h3 className="sec">Prüfbereiche</h3>
      <div className="card" style={{ padding: 0 }}>
        {schritte.map((s) => (
          <div key={s.nummer} className="protokoll-schritt">
            <div className={`schritt-nummer ${s.status}`} aria-hidden="true">
              {s.status === 'erledigt' ? '✓' : s.nummer}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <b>{s.titel}</b>
              <div className="s" style={{ margin: 0 }}>
                {s.status === 'erledigt'
                  ? `Abgeschlossen${s.erledigtVon ? ` von ${s.erledigtVon}` : ''}${s.erledigtAm ? ` am ${datum(s.erledigtAm)}` : ''}`
                  : s.status === 'laufend'
                    ? 'Begonnen'
                    : 'Noch offen'}
                {s.anmerkung ? ` — ${s.anmerkung}` : ''}
              </div>
            </div>
            <button className="btn ghost sm" onClick={() => onSchritt(s.nummer)}>
              Öffnen
            </button>
          </div>
        ))}
        <p className="note" style={{ padding: '0 16px 12px' }}>
          Markieren Sie einen Bereich als geprüft, sobald Sie seine Unterlagen durchgesehen haben. Ein Haken lässt sich wieder öffnen.
        </p>
      </div>

      <h3 className="sec">Prüfung dokumentieren</h3>
      <div className="card" style={{ padding: '16px 18px' }}>
        {abgeschlossen ? (
          <>
            <div className={`check ${pruefung?.urteil === 'ohne_einwand' ? 'ja' : 'nein'}`}>
              <span className="bx">{pruefung?.urteil === 'ohne_einwand' ? '✓' : '!'}</span>
              <span style={{ flex: 1 }}>
                Abgeschlossen am {datum(pruefung?.abgeschlossen_am)} —{' '}
                {pruefung?.urteil === 'ohne_einwand' ? 'ohne Einwand' : 'mit Vorbehalt'}
              </span>
            </div>
            {pruefung?.gesamtanmerkung && <p className="s">{pruefung.gesamtanmerkung}</p>}
            {!DEMO && (
              <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                <a className="btn primary" href={api.protokollUrl(pruefungId)} target="_blank" rel="noreferrer">
                  Protokoll mit Bericht öffnen
                </a>
                <a className="btn" href={api.protokollUrl(pruefungId)} download>
                  Herunterladen
                </a>
              </div>
            )}
          </>
        ) : (
          <>
            <Bedingung erfuellt={alleSchritte} text="Prüfbereiche 1 bis 4 abgeschlossen" wert={`${Math.min(erledigt, 4)} / 4`} />
            <Bedingung
              erfuellt={(kennzahlen?.belegeOffen ?? 1) === 0}
              text="Alle Belege haben einen Status"
              wert={kennzahlen ? `${kennzahlen.belegeGeprueft + kennzahlen.belegeRueckfrage} / ${kennzahlen.belegeGesamt}` : '—'}
            />
            <Bedingung erfuellt={offene === 0} text="Keine offenen Rückfragen" wert={offene === 0 ? 'erledigt' : `${offene} offen`} />

            {rolle === 'beirat' ? (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 16 }}>
                <button
                  className="btn primary"
                  disabled={offene > 0 || !alleSchritte}
                  onClick={() => {
                    setAnmerkung('');
                    setEntlastung(true);
                    setAbschluss(true);
                  }}
                >
                  Prüfung abschließen
                </button>
                <button
                  className="btn ghost"
                  onClick={() => {
                    setAnmerkung(offene > 0 ? `${offene} ${offene === 1 ? 'Rückfrage ist' : 'Rückfragen sind'} noch offen.` : '');
                    setEntlastung(false);
                    setAbschluss(true);
                  }}
                >
                  Mit Vorbehalt abschließen
                </button>
                <span style={{ fontSize: 12.5, color: 'var(--tx-2)' }}>
                  {offene > 0 || !alleSchritte ? 'Für den Abschluss ohne Einwand müssen die Bereiche 1–4 abgeschlossen und alle Rückfragen erledigt sein.' : 'Alle Vorbedingungen erfüllt.'}
                </span>
              </div>
            ) : (
              <p className="note">Den Abschluss nimmt der Beirat vor, nicht die Hausverwaltung.</p>
            )}
          </>
        )}
      </div>

      <div className="hinweisbox">
        <b>Hinweis</b>
        <p>
          Das digitale Protokoll hält Ihre Prüfschritte fest; die Entscheidung über Abrechnung und Entlastung trifft die
          Eigentümerversammlung. Mit dem Abschluss entsteht der „Bericht des Beirates über die Buch- und
          Wirtschaftsplanprüfung" als letzte Seite des Protokolls, mit Unterschriftenzeilen.
        </p>
      </div>

      {abschluss && (
        <div className="ovl" onMouseDown={(e) => e.target === e.currentTarget && setAbschluss(false)}>
          <div className="sheet">
            <h3>{offene > 0 || !alleSchritte ? 'Mit Vorbehalt abschließen' : 'Prüfung abschließen'}</h3>
            <p className="s">
              {offene > 0 || !alleSchritte
                ? 'Die offenen Punkte werden als solche in Protokoll und Bericht übernommen. Die Eigentümerversammlung wird dadurch nicht blockiert.'
                : 'Alle Bereiche sind geprüft, alle Rückfragen erledigt. Der Bericht des Beirats wird mit dem Prüfungsvermerk erzeugt.'}
            </p>
            <label className="feld">
              <span>Prüfer</span>
              <input value={name} readOnly />
              <small>Alle eingeladenen Beiräte erscheinen im Bericht.</small>
            </label>
            <label className="feld">
              <span>Ort der Prüfung (optional)</span>
              <input value={ort} onChange={(e) => setOrt(e.target.value)} placeholder="leer = online geprüft" />
            </label>
            <label className="feld" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={entlastung} onChange={(e) => setEntlastung(e.target.checked)} style={{ width: 'auto' }} />
              <span style={{ margin: 0 }}>Entlastung des Verwalters empfehlen</span>
            </label>
            <textarea value={anmerkung} onChange={(e) => setAnmerkung(e.target.value)} placeholder="Gesamtanmerkung des Beirats …" />
            <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
              <button className="btn ghost" onClick={() => setAbschluss(false)}>
                Abbrechen
              </button>
              <button className="btn primary" disabled={laeuft} onClick={() => abschliessen(offene > 0 || !alleSchritte ? 'mit_vorbehalt' : 'ohne_einwand')}>
                {offene > 0 || !alleSchritte ? 'Mit Vorbehalt abschließen' : 'Ohne Einwand abschließen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Zahl({ wert, label, ton }: { wert: string; label: string; ton?: 'w' }) {
  return (
    <div className="kpi">
      <div className="v" style={ton === 'w' ? { color: 'var(--warn)' } : undefined}>{wert}</div>
      <div className="l">{label}</div>
    </div>
  );
}

function Bedingung({ erfuellt, text, wert }: { erfuellt: boolean; text: string; wert: string }) {
  return (
    <div className={`check ${erfuellt ? 'ja' : 'nein'}`}>
      <span className="bx">{erfuellt ? '✓' : '!'}</span>
      <span style={{ flex: 1 }}>{text}</span>
      <span style={{ color: erfuellt ? 'var(--tx-2)' : 'var(--warn)' }}>{wert}</span>
    </div>
  );
}
